/* ==========================================================================
   Makefield — map section
   --------------------------------------------------------------------------
   Loads the Google Maps JS API on demand and centres a styled map on the
   section's address. The background image stays behind the map, so the band
   never flashes empty while the API loads and still reads correctly if the
   request fails.

   Only sections that actually have a key and an address render a [data-map]
   element, so this script no-ops on a page without one.
   ========================================================================== */

(function () {
  'use strict';

  var apiPromise = null;

  /* One <script> for the whole page however many maps are on it; the API
     throws if it is loaded twice. */
  function loadApi(key) {
    if (apiPromise) return apiPromise;

    apiPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(key) +
        '&libraries=geocoding&loading=async';
      script.async = true;
      script.onload = resolve;
      script.onerror = function () {
        reject(new Error('Google Maps failed to load'));
      };
      document.head.appendChild(script);
    });

    return apiPromise;
  }

  var STYLES = {
    light: [
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] }
    ],
    dark: [
      { elementType: 'geometry', stylers: [{ color: '#212121' }] },
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] }
    ]
  };

  function init(root) {
    var el = root.querySelector('[data-map]');
    if (!el || el.dataset.mkMapReady === 'true') return;

    var key = el.getAttribute('data-api-key');
    var address = el.getAttribute('data-address-setting');
    if (!key || !address) return;

    el.dataset.mkMapReady = 'true';

    loadApi(key)
      .then(function () {
        var geocoder = new google.maps.Geocoder();

        geocoder.geocode({ address: address }, function (results, status) {
          if (status !== 'ok' && status !== 'OK') {
            /* Leave the background image in place — a failed lookup should
               look like the static design, not a grey box. */
            el.dataset.mkMapReady = 'false';
            return;
          }

          var position = results[0].geometry.location;

          /* Fixed, not interactive: the design's location card is an overlay
             pinned to a percentage of the canvas, so a map the visitor could
             pan or zoom would leave the card pointing at the wrong place. */
          var map = new google.maps.Map(el, {
            center: position,
            zoom: Number(root.getAttribute('data-map-zoom')) || 14,
            styles: STYLES[root.getAttribute('data-map_style')] || STYLES.light,
            disableDefaultUI: true,
            draggable: false,
            scrollwheel: false,
            gestureHandling: 'none',
            keyboardShortcuts: false
          });

          /* The map is only revealed once it has tiles, so the image behind it
             carries the band until then. */
          root.classList.add('mk-map--loaded');

          /* Re-centre after a resize so the pin does not drift off-screen when
             the canvas changes shape at a breakpoint. */
          window.addEventListener('resize', function () {
            map.setCenter(position);
          });
        });
      })
      .catch(function () {
        el.dataset.mkMapReady = 'false';
      });
  }

  function initAll() {
    document.querySelectorAll('[data-section-type="map"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector) {
      event.target.querySelectorAll('[data-section-type="map"]').forEach(init);
    }
  });
})();
